import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { ErrorBanner, SuccessBanner } from "../components/ui";

function BrandFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-tora-paper">
      <header className="bg-white border-b-2 border-tora">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <img src="/logo-header.jpg" alt="令和の虎 経営塾" className="h-10 md:h-12 w-auto" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="card w-full max-w-md p-6 md:p-8 shadow-md">
          {children}
        </div>
      </main>
    </div>
  );
}

export function PasswordResetConfirmPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") ?? "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <BrandFrame>
        <p className="text-[11px] text-tora tracking-widest font-semibold">
          PASSWORD RESET
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-tora-ink mt-1.5 mb-4">
          パスワード再設定
        </h1>
        <ErrorBanner>
          トークンがありません。メール内のリンクから開き直してください。
        </ErrorBanner>
        <p className="mt-5 text-center">
          <Link
            to="/password-reset"
            className="text-sm text-tora hover:underline"
          >
            メール送信をやり直す
          </Link>
        </p>
      </BrandFrame>
    );
  }

  return (
    <BrandFrame>
      <p className="text-[11px] text-tora tracking-widest font-semibold">
        PASSWORD RESET
      </p>
      <h1 className="text-2xl md:text-3xl font-bold text-tora-ink mt-1.5">
        新しいパスワードを設定
      </h1>
      <p className="text-sm text-tora-ink/70 mt-1 mb-6">
        8 文字以上で、推測されにくい文字列をご利用ください。
      </p>

      {done ? (
        <>
          <SuccessBanner>
            パスワードを更新しました。ログインし直してください。
          </SuccessBanner>
          <p className="mt-5 text-center">
            <Link to="/login" className="text-sm text-tora hover:underline">
              ログインへ →
            </Link>
          </p>
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            if (pw1.length < 8) {
              setErr("パスワードは 8 文字以上で入力してください");
              return;
            }
            if (pw1 !== pw2) {
              setErr("パスワードが一致しません");
              return;
            }
            setBusy(true);
            try {
              await api.post("/auth/password-reset/confirm", {
                token,
                newPassword: pw1,
              });
              setDone(true);
              setTimeout(() => nav("/login", { replace: true }), 2500);
            } catch (e) {
              setErr((e as Error).message ?? "再設定に失敗しました");
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="mb-4">
            <label className="label" htmlFor="pw1">
              新しいパスワード
            </label>
            <input
              id="pw1"
              type="password"
              className="input"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              autoFocus
            />
            <p className="text-xs text-tora-ink/50 mt-1">
              8 文字以上 / 英数字記号の組合せ推奨
            </p>
          </div>
          <div className="mb-4">
            <label className="label" htmlFor="pw2">
              新しいパスワード (確認)
            </label>
            <input
              id="pw2"
              type="password"
              className="input"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          {err && <ErrorBanner className="mb-3">{err}</ErrorBanner>}
          <button className="btn-primary btn-lg w-full" disabled={busy}>
            {busy ? "更新中…" : "パスワードを更新 →"}
          </button>
          <p className="mt-5 text-center">
            <Link
              to="/login"
              className="text-sm text-tora-ink/60 hover:text-tora"
            >
              ログイン画面へ戻る
            </Link>
          </p>
        </form>
      )}
    </BrandFrame>
  );
}
